import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  debounce,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { data } from './makeData';
import { useAPI } from '../../../shared/services/api/API';
import { OrderType, WASHUB_CONSTANTS } from '../../../shared/Constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { BusinessOrderList } from './business-store-order-list/BusinessOrderList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

export type BusinessStoreOrderDeatils = {
  id: string;
  name: string;
  description: string;
  price: string;
  categoryId: string;
  mediaItemId: string;
  orderId: string;
  orderDate: string;
  orderStatus: string;
};
const TABLE_PAGE_SIZE = 10;
const API_BATCH_SIZE = 1000;

export const BusinessStoreOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const BusinessStoreOrder = location?.state?.order;
  const BusinessDetail = location?.state?.orderDetails;
  const { httpPost, httpGet, handleAlertBar  } = useAPI();
  const [businessId, setBusinessId] = useState('');
  const [businessList, setBusinessList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [businessStoreId, setBusinessStoreId] = useState('');
  const [businessStoreData, setBusinessStoreData] = useState([]);
  const [tableData, setTableData] = useState<BusinessStoreOrderDeatils[]>(
    () => data,
  );
  const [IsLoading, setIsLoading] = useState(false);
  const type = localStorage.getItem('userType');
  const [ filterStatus, setFilterStatus] = useState<any>('ALL');
  const [filterStatusList, setFilterStatusList] = useState<any>([
    'REWASH',
    'READYTODELIVER_PARTIAL',
    'READYTODELIVER',
    'DELIVERED',
    'DELIVERED_PARTIAL',
    'CANCELLED',
  ]);

  const [orderId, setOrderId] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const queryParams = {
    page: 0,
    size: 1000,
    sortColumn: 'modifiedAt',
  };

  // ------------------------------------------------------------
  // Business change
  // ------------------------------------------------------------

  useEffect(() => {
    if (!businessId) {
      setBusinessStoreData([]);
      setDepartmentList([]);
      setBusinessStoreId('');
      setDepartmentId('');
      setTableData([]);
      setOrderId('');
      setFilterStatus('ALL');
      return;
    }

    // Reset dependent selections/data
    setBusinessStoreId('');
    setDepartmentId('');
    setBusinessStoreData([]);
    setDepartmentList([]);
    setTableData([]);
    setOrderId('');
    setFilterStatus('ALL');

    fetchBusinessStore(businessId);
  }, [businessId]);

  // ------------------------------------------------------------
  // Edit order / navigation state
  // ------------------------------------------------------------

  useEffect(() => {
    const returnedBusinessId =
      BusinessStoreOrder?.business?.id ||
      BusinessDetail?.businessStore?.business?.id;

    const returnedBusinessStoreId =
      BusinessStoreOrder?.businessStore?.id ||
      BusinessDetail?.businessStore?.id;

    const returnedDeptFilter = location?.state?.initialDeptFilter || '';
    if (returnedBusinessId) {
      setBusinessId(returnedBusinessId);
    }
    if (returnedBusinessStoreId) {
      setBusinessStoreId(returnedBusinessStoreId);
    }
    setDepartmentId(returnedDeptFilter);
  }, [BusinessStoreOrder, BusinessDetail, location?.state]);

  // ------------------------------------------------------------
  // Fetch business list
  // ------------------------------------------------------------

  useEffect(() => {
    fetchBusiness();
  }, []);

  // ------------------------------------------------------------
  // Business Store change
  // ------------------------------------------------------------

  useEffect(() => {
    if (!businessStoreId) {
      setDepartmentList([]);
      setDepartmentId('');
      return;
    }

      fetchBusinessStoreDepartmentData(businessStoreId);
      const returnedDeptFilter = location?.state?.initialDeptFilter;
      if (!departmentId && !returnedDeptFilter) {
        fetchBusinessStoreOrder(businessStoreId, 0, API_BATCH_SIZE);
      }
      setOrderId('');
      setFilterStatus('ALL');
  }, [businessStoreId]);

  // ------------------------------------------------------------
  // Department change
  // ------------------------------------------------------------

  useEffect(() => {
    if (!departmentId) {
      return;
    }

    setOrderId('');
    setFilterStatus('ALL');
    fetchBusinessDepartmentOrder(departmentId, 0, API_BATCH_SIZE);
  }, [departmentId]);

  // ------------------------------------------------------------
  // GET Business
  // ------------------------------------------------------------

  const fetchBusiness = async () => {
    const url = `${WASHUB_CONSTANTS.API_URLS.BUSINESS_DATA_FOR_BUSINESS_GET}`;
    httpGet(url, queryParams).then((response) => {
      if (response.status === 200) {
        const sortBusinessArray =
          response?.data?.sort((a: any, b: any) =>
            a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
          ) || [];

        setBusinessList(sortBusinessArray);
      } else if (response?.data?.error) {
        console.error('Error fetching Business: ', response.data.message);
      }
    });
  };

  // ------------------------------------------------------------
  // GET Business Store
  // ------------------------------------------------------------

  const fetchBusinessStore = async (id: string) => {
    const url = `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTORE_DATA_FOR_BUSINESS_GET}`;
    httpGet(url + '/' + id, queryParams).then((response) => {
      if (response.status === 200) {
        const sortBusinessStoreArray =
          response?.data?.sort((a: any, b: any) =>
            a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
          ) || [];

        setBusinessStoreData(sortBusinessStoreArray);
      } else if (response?.data?.error) {
        console.error('Error fetching BusinessStore: ', response.data.message);
      }
    });
  };

  // ------------------------------------------------------------
  // GET Business Store Orders
  // ------------------------------------------------------------

  const fetchBusinessStoreOrder = async (
    id: string,
    pageNum = 0,
    pageSize = API_BATCH_SIZE,
  ) => {
    setBusinessStoreId(id);
    setIsLoading(true);
    const storeQueryParams = {
      page: pageNum,
      size: pageSize,
      sortColumn: 'modifiedAt',
    };

    httpGet(
      WASHUB_CONSTANTS.API_URLS.BUSINESSSTOREORDER_DATA_GET +
        '/' +
        businessId +
        '/' +
        id,
      storeQueryParams,
    )
      .then((response) => {
        if (response.status === 200) {
          setTableData(response?.data || []);
        } else {
          setTableData([]);
          if (response?.data?.error) {
            console.error(
              'Error fetching BusinessStoreOrder: ',
              response.data.message,
            );
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching BusinessStoreOrder:', error);
        setTableData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // ------------------------------------------------------------
  // GET Business Department Orders
  // ------------------------------------------------------------

  const fetchBusinessDepartmentOrder = async (
    id: string,
    pageNum = 0,
    pageSize = API_BATCH_SIZE,
  ) => {
    setIsLoading(true);
    const deptQueryParams = {
      page: pageNum,
      size: pageSize,
      sortColumn: 'modifiedAt',
    };

    httpGet(
      WASHUB_CONSTANTS.API_URLS.BUSINESS_ORDER_DATA_GET +
        '/' +
        businessId +
        '/' +
        businessStoreId +
        '/' +
        id,
      deptQueryParams,
    )
      .then((response) => {
        if (response.status === 200) {
          setTableData(response?.data || []);
        } else {
          setTableData([]);

          if (response?.data?.error) {
            console.error(
              'Error fetching Business Order: ',
              response.data.message,
            );
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching Business Order:', error);
        setTableData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // ------------------------------------------------------------
  // GET Departments
  // ------------------------------------------------------------

  const fetchBusinessStoreDepartmentData = async (storeId: string) => {
    httpGet(
      `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTORE_DEPARTMENT_DATA_GET}/${storeId}`,
      queryParams,
    ).then((response) => {
      if (response.status === 200) {
        const sortBusinessDeptArray =
          response?.data?.sort((a: any, b: any) =>
            a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
          ) || [];

        setDepartmentList(sortBusinessDeptArray);

        const returnedDeptFilter = location?.state?.initialDeptFilter;

        if (returnedDeptFilter) {
          setDepartmentId(returnedDeptFilter);
        }
      } else if (response?.data?.error) {
        console.error(
          'Error fetching Business Store Department: ',
          response.data.message,
        );
      }
    });
  };

  // ------------------------------------------------------------
  // Status Filter
  // ------------------------------------------------------------

  const updateFilterStatus = (value: string) => {
    setFilterStatus(value);
    // Always reset search when status changes
    setOrderId('');
    if (value === 'ALL') {
      if (departmentId) {
        fetchBusinessDepartmentOrder(departmentId, 0, API_BATCH_SIZE);
      } else if (businessStoreId) {
        fetchBusinessStoreOrder(businessStoreId, 0, API_BATCH_SIZE);
      } else {
        setTableData([]);
      }
      return;
    }
    if (!businessId || !businessStoreId) {
      return;
    }

    const payload = {
      status: value,
      businessId: businessId,
      businessStoreId: businessStoreId,
      businessDeptId: departmentId,
    };

    setIsLoading(true);

    httpPost(
      `${WASHUB_CONSTANTS.API_URLS.BUSINESSSTOREORDER_STATUS_FILTER_POST}`,
      payload,
    )
      .then((response) => {
        if (response?.status === 200) {
          setTableData(response?.data || []);
        } else {
          setTableData([]);
        }
      })
      .catch((error: any) => {
        setTableData([]);
        handleAlertBar('error', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // ------------------------------------------------------------
  // Refresh
  // ------------------------------------------------------------

  const refetch = () => {
    setOrderId('');
    setFilterStatus('ALL');

    setRefreshTrigger((prev) => prev + 1);

    if (departmentId) {
      fetchBusinessDepartmentOrder(departmentId, 0, API_BATCH_SIZE);
    } else if (businessStoreId) {
      fetchBusinessStoreOrder(businessStoreId, 0, API_BATCH_SIZE);
    } else {
      setTableData([]);
    }
  };

  // ------------------------------------------------------------
  // Search Order
  // ------------------------------------------------------------

  const fetchBusinessOrderById = useMemo(
    () =>
      debounce(
        (
          searchedOrderId: string,
          selectedBusinessId: string,
          selectedBusinessStoreId: string,
          selectedDepartmentId: string,
        ) => {
          if (!searchedOrderId) {
            return;
          }

          if (!selectedBusinessId) {
            handleAlertBar(
              'error',
              'Please Select business then search order Id',
            );
            return;
          }

          const payload = {
            businessId: selectedBusinessId,
            businessStoreId: selectedBusinessStoreId,
            businessDeptId: selectedDepartmentId,
            orderId: searchedOrderId,
          };

          setIsLoading(true);

          httpPost(
            WASHUB_CONSTANTS.API_URLS.SEARCH_BUSINESS_ORDER_BY_ORDERID_POST,
            payload,
          )
            .then((response) => {
              if (response.status === 200) {
                setTableData(response?.data || []);
              } else {
                setTableData([]);

                if (response?.data?.error) {
                  handleAlertBar('error', 'Please Select business');
                }
              }
            })
            .catch((error: any) => {
              setTableData([]);
              handleAlertBar('error', error);
            })
            .finally(() => {
              setIsLoading(false);
            });
        },
        700,
      ),
    [httpPost, handleAlertBar],
  );

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------

  return (
    <div
      style={{
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Grid container>
        <Grid item className="mb-1 mt-1 ms-3">
          <Typography
            sx={{ textAlign: 'center' }}
            variant="h5"
            className="mt-0"
          >
            Business Orders
          </Typography>
        </Grid>
      </Grid>

      {/* ---------------------------------------------------- */}
      {/* Main Filters */}
      {/* ---------------------------------------------------- */}

      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          padding: '2px 15px',
          minWidth: {
            xs: '300px',
            sm: '360px',
            md: '400px',
          },
          gap: '1rem',
        }}
      >
        {/* Business */}

        <TextField
          size="small"
          label="Select Business"
          name="business"
          select
          style={{ width: '30%' }}
          value={businessId}
          onChange={(e) => {
            setBusinessId(e.target.value);
          }}
        >
          {businessList?.map((option: any) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Business Store */}

        <TextField
          size="small"
          label="Select Business Store"
          name="businessStore"
          select
          style={{ width: '30%' }}
          value={businessStoreId}
          disabled={!businessId}
          onChange={(e) => {
            setBusinessStoreId(e.target.value);
            setDepartmentId('');
            setDepartmentList([]);
            setTableData([]);
            setOrderId('');
            setFilterStatus('ALL');
          }}
        >
          {businessStoreData.map((option: any) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Department */}

        <TextField
          size="small"
          label="Select Department"
          name="department"
          select
          style={{ width: '30%' }}
          value={departmentId}
          disabled={!businessStoreId}
          onChange={(e) => {
            setDepartmentId(e.target.value);
            setOrderId('');
            setFilterStatus('ALL');
          }}
        >
          {departmentList.map((option: any) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Status */}

        <TextField
          size="small"
          label="Select Status"
          name="filterStatus"
          select
          style={{ width: '30%' }}
          value={filterStatus}
          disabled={!businessStoreId}
          onChange={(e) => {
            updateFilterStatus(e.target.value);
          }}
        >
          <MenuItem value="ALL">ALL</MenuItem>

          {filterStatusList.map((option: any) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* ---------------------------------------------------- */}
      {/* Search + Create + Refresh */}
      {/* ---------------------------------------------------- */}

      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 15px',
          minWidth: {
            xs: '300px',
            sm: '360px',
            md: '400px',
          },
          gap: '1rem',
        }}
      >
        <div style={{ width: '76%' }}>
          {/* Order Search */}

          <TextField
            size="small"
            className="me-3"
            style={{
              width: '31.5%',
            }}
            label="Select Order Id"
            name="orderId"
            variant="outlined"
            value={orderId}
            disabled={!businessId}
            onChange={(e) => {
              const value = e.target.value;

              setOrderId(value);

              if (value) {
                fetchBusinessOrderById(
                  value,
                  businessId,
                  businessStoreId,
                  departmentId,
                );
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),

              endAdornment: orderId ? (
                <InputAdornment position="end">
                  <Link
                    component="button"
                    title="Clear"
                    onClick={(e) => {
                      e.preventDefault();
                      refetch();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </Link>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Create Order */}

          <Button
            color="primary"
            sx={{
              color: 'white',
            }}
            variant="contained"
            onClick={() => {
              navigate('orders', {
                state: {
                  edit: false,
                  orderType: OrderType.Business,
                  order: {
                    business: {
                      id: businessId,
                    },
                    businessStore: {
                      id: businessStoreId,
                    },
                    department: {
                      id: departmentId,
                    },
                  },
                  initialDeptFilter: departmentId,
                },
              });
            }}
          >
            Create Business Order
          </Button>
        </div>

        {/* Refresh */}

        <Tooltip arrow title="Refresh Table Data">
          <IconButton onClick={refetch}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ---------------------------------------------------- */}
      {/* Orders Table */}
      {/* ---------------------------------------------------- */}

      <BusinessOrderList
        setTableData={setTableData}
        fetchBusinessDepartmentOrder={fetchBusinessDepartmentOrder}
        fetchBusinessStoreOrder={fetchBusinessStoreOrder}
        fetchBusinessOrderById={fetchBusinessOrderById}
        tableData={tableData}
        OrderType={OrderType}
        businessId={businessId}
        businessStoreId={businessStoreId}
        departmentId={departmentId}
        orderId={orderId}
        IsLoading={IsLoading}
        refreshTrigger={refreshTrigger}
        filterStatus={filterStatus}
      />
    </div>
  );
};
